import { useCallback, useEffect, useState } from 'react';
import { usePrevious } from 'react-use';

import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';
import type { TransactionEditDialogProps } from '../types';
import { showTxnDetailsDialog } from '../actions';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';

type Params = Pick<
  TransactionEditDialogProps,
  'defaultAccountId' | 'focusSplits' | 'onClose' | 'recurring' | 'txnActionCallback' | 'wrapperId'
> & {
  closeAll: () => void;
  connectedTxn: CashFlowTransaction | undefined;
  dispatch: (action: unknown) => void;
  refreshTransaction?: (txn: CashFlowTransaction) => void;
};

export function useStaleTransactionRefresh({
  closeAll,
  connectedTxn,
  defaultAccountId,
  dispatch,
  focusSplits,
  onClose,
  refreshTransaction,
  recurring,
  txnActionCallback,
  wrapperId,
}: Params): { isDirtyOutside: boolean; onRefreshClick: () => void } {
  const previousTransaction = usePrevious(connectedTxn);
  const [isDirtyOutside, setIsDirtyOutside] = useState(false);

  useEffect(() => {
    if (previousTransaction && connectedTxn !== previousTransaction) {
      setIsDirtyOutside(true);
    }
  }, [connectedTxn, previousTransaction]);

  const onRefreshClick = useCallback(() => {
    if (refreshTransaction) {
      if (connectedTxn) {
        refreshTransaction(connectedTxn);
      } else {
        dispatchSimpleNotification('Transaction synced');
      }
      setIsDirtyOutside(false);
      return;
    }

    closeAll();
    if (connectedTxn) {
      setTimeout(() => {
        dispatch(
          showTxnDetailsDialog({
            onClose,
            recurring,
            defaultAccountId,
            focusSplits,
            wrapperId,
            txnActionCallback,
            txn: connectedTxn,
          }),
        );
      }, 0);
    } else {
      dispatchSimpleNotification('Transaction synced');
    }
  }, [
    closeAll,
    connectedTxn,
    defaultAccountId,
    dispatch,
    focusSplits,
    onClose,
    refreshTransaction,
    recurring,
    txnActionCallback,
    wrapperId,
  ]);

  return { isDirtyOutside, onRefreshClick };
}
